/**
 * Event presentation units shared by the organizer pages.
 *
 * The status badge and the list row are the two shapes that more than one page
 * renders, so they live here instead of inside the page that happened to need
 * them first.
 */

import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { EventStatus, EventSummaryDto } from '@shared/types'

import { currentLocale } from '../../lib/i18n'
import { Badge, type BadgeProps } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { formatLocalShort } from '../../lib/format'

/** Draft is neutral, collecting is in progress, decided is a win, archived is past. */
const STATUS_VARIANT: Record<EventStatus, BadgeProps['variant']> = {
  draft: 'secondary',
  collecting: 'default',
  decided: 'success',
  archived: 'outline',
}

export function EventStatusBadge({ status, className }: { status: EventStatus; className?: string }) {
  const { t } = useTranslation()
  return (
    <Badge variant={STATUS_VARIANT[status]} className={className}>
      {t(`status.${status}`)}
    </Badge>
  )
}

/**
 * One event as a single link target.
 *
 * The whole card is the link so the row stays reachable on touch screens, and the
 * counts stay inline text because a list must not grow a second navigation layer.
 * The destination is the caller's choice: an event the reader organizes opens the
 * organizer console, while one they only joined opens its share page, the only
 * surface a member may act on.
 */
export function EventRowCard({ event, to }: { event: EventSummaryDto; to?: string }) {
  const { t } = useTranslation()
  const locale = currentLocale()

  return (
    <Link
      to={to ?? `/me/events/${event.id}`}
      // The caller lays the rows out in a grid, and a grid item keeps its
      // content-based minimum until it is allowed to shrink: the unbreakable
      // title raised that floor above the column and pushed the whole page
      // sideways at 320px.
      className="focus-visible:ring-ring block min-w-0 rounded-xl focus-visible:ring-2 focus-visible:outline-none"
    >
      <Card className="hover:bg-accent/40 h-full transition-colors">
        {/*
          The title is the only part that may give way, and it can only do so once
          it is allowed to shrink: without `min-w-0` a flex item keeps its
          content-based minimum, so an unbreakable Chinese title pushed the header
          — and with it the page — past the viewport at 320px. The status badge
          is the opposite: it never gives way.
        */}
        <CardHeader className="flex-row items-start justify-between gap-3">
          <CardTitle className="min-w-0 truncate">{event.title}</CardTitle>
          <EventStatusBadge status={event.status} className="shrink-0" />
        </CardHeader>
        <CardContent className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span>{formatLocalShort(event.eventDateStart, locale)}</span>
          <span>
            {t('events.peopleAndUnits', {
              people: t('events.participantCount', { count: event.peopleCount }),
              units: t('events.unitCount', { count: event.unitCount }),
            })}
          </span>
          <span>{t('events.roundCount', { count: event.roundCount })}</span>
        </CardContent>
      </Card>
    </Link>
  )
}
